import { Col, Text } from 'components';
import {
  createProfanityTerm,
  getProfanityTerms,
  removeProfanityTerm,
  theme,
  updateProfanityTerm
} from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { LEVEL_LABELS, PROFANITY_LEVELS } from '../constants';
import Pagination from './Pagination';
import TermModal from './TermModal';
import TermsTable from './TermsTable';
import { AccessDenied, ErrorState } from './ui';

const DEFAULT_FILTERS = { level: 'all', active: 'all', search: '' };
const FILTER_KEYS = Object.keys(DEFAULT_FILTERS);
const DEFAULT_LIMIT = 50;
const TAB = 'profanity';

const selectCls =
  'h-[34px] border border-[#D2D8DF] rounded-lg px-2.5 text-[13px] text-[#353E4A] bg-white focus:outline-none focus:ring-2 focus:ring-[#FA3F58]/20';
const inputCls =
  'h-[34px] border border-[#D2D8DF] rounded-lg px-3 text-[13px] text-[#353E4A] bg-white focus:outline-none focus:ring-2 focus:ring-[#FA3F58]/20';
const labelCls = 'text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide';

const ProfanityTab = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the single source of truth for the applied query.
  const applied = FILTER_KEYS.reduce((acc, key) => {
    acc[key] = searchParams.get(key) || DEFAULT_FILTERS[key];
    return acc;
  }, {});
  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;
  const appliedKey = searchParams.toString();

  // The search text is kept locally so typing feels instant, then written to
  // the URL (debounced) so it doesn't refetch on every keystroke.
  const [searchInput, setSearchInput] = useState(applied.search);
  const searchTimer = useRef(null);

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [modal, setModal] = useState(null); // { term? } — open add/edit modal
  const [submitting, setSubmitting] = useState(false);
  const reqIdRef = useRef(0);

  const fetchTerms = async (f, p, lim) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    const res = await getProfanityTerms({ ...f, page: p, limit: lim });
    if (reqId !== reqIdRef.current) return; // superseded

    if (res?.status === 403) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    if (res?.err || !res?.data) {
      setError(res?.err || 'Could not load profanity terms.');
      setLoading(false);
      return;
    }
    setItems(res.data.items || []);
    setPagination(res.pagination || null);
    setLoading(false);
  };

  useEffect(() => {
    fetchTerms(applied, page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey]);

  // Keep the local search input in sync when the URL changes externally.
  useEffect(() => {
    setSearchInput(applied.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied.search]);

  useEffect(() => () => searchTimer.current && clearTimeout(searchTimer.current), []);

  /** Merge updates into the current URL params, dropping empty / "all" values. */
  const writeParams = (updates) => {
    const next = { tab: TAB };
    searchParams.forEach((v, k) => {
      next[k] = v;
    });
    Object.entries(updates).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '' || v === 'all') delete next[k];
      else next[k] = String(v);
    });
    setSearchParams(next, { replace: true });
  };

  const handleFilterChange = (key, value) => writeParams({ [key]: value, page: 1 });

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => writeParams({ search: value, page: 1 }), 400);
  };

  const handleClear = () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSearchInput('');
    setSearchParams({ tab: TAB });
  };

  const patchItem = (id, updates) =>
    setItems((prev) => prev.map((t) => (t._id === id ? { ...t, ...updates } : t)));

  const handleToggleActive = async (term) => {
    if (busyId) return;
    setBusyId(term._id);
    const res = await updateProfanityTerm(term._id, { active: !term.active });
    setBusyId(null);

    if (res?.err || !res?.data) {
      dispatch({ type: 'HANDLE_RES', payload: { err: res?.err || 'Update failed.' } });
      return;
    }
    patchItem(term._id, { active: res.data.active, updatedAt: res.data.updatedAt });
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: `Term ${res.data.active ? 'activated' : 'deactivated'}.` }
    });
  };

  const handleDelete = async (term) => {
    if (busyId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${term.term}" permanently? Deactivating is usually safer.`))
      return;
    setBusyId(term._id);
    const res = await removeProfanityTerm(term._id);
    setBusyId(null);

    if (res?.err) {
      dispatch({ type: 'HANDLE_RES', payload: { err: res.err || 'Delete failed.' } });
      return;
    }
    setItems((prev) => prev.filter((t) => t._id !== term._id));
    setPagination((prev) => (prev ? { ...prev, total: Math.max(0, (prev.total || 1) - 1) } : prev));
    dispatch({ type: 'HANDLE_RES', payload: { res: 'Term deleted.' } });
  };

  const handleSubmitModal = async ({ term, level }) => {
    if (submitting) return;
    setSubmitting(true);
    const editing = modal?.term;
    const res = editing
      ? await updateProfanityTerm(editing._id, { level })
      : await createProfanityTerm({ term, level });
    setSubmitting(false);

    if (res?.err || !res?.data) {
      dispatch({
        type: 'HANDLE_RES',
        payload: { err: res?.err || (editing ? 'Update failed.' : 'Could not add the term.') }
      });
      return;
    }

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: res.msg || (editing ? 'Term updated.' : 'Term added.') }
    });
    setModal(null);

    if (editing) {
      patchItem(editing._id, { level: res.data.level, updatedAt: res.data.updatedAt });
    } else {
      // New term's page/sort position is unknown — refetch the current view.
      fetchTerms(applied, page, limit);
    }
  };

  const total = pagination?.total ?? 0;
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;

  if (forbidden) return <AccessDenied />;

  return (
    <Col noFlex wid="100%">
      {/* Filter bar */}
      <div className="bg-white border border-[#D2D8DF] rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className={labelCls}>Level</label>
            <select
              value={applied.level}
              onChange={(e) => handleFilterChange('level', e.target.value)}
              className={selectCls}
            >
              <option value="all">All levels</option>
              {PROFANITY_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {LEVEL_LABELS[l]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={labelCls}>Status</label>
            <select
              value={applied.active}
              onChange={(e) => handleFilterChange('active', e.target.value)}
              className={selectCls}
            >
              <option value="all">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={labelCls}>Search</label>
            <div className="relative flex items-center">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="term starts with…"
                className={`${inputCls} pr-7 w-[220px] font-mono`}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="absolute right-2 text-[#97A3B5] hover:text-[#353E4A] transition-colors"
                  aria-label="Clear search"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            <Text c1 col={theme.GREY_60} style={{ fontSize: '12px' }}>
              {total} terms
            </Text>
            <button
              type="button"
              onClick={handleClear}
              className="h-[34px] px-4 rounded-lg border border-[#D2D8DF] text-[13px] text-[#5F6F86] font-medium hover:bg-[#F0F2F4] transition-colors"
            >
              Clear filters
            </button>
            <button
              type="button"
              onClick={() => setModal({})}
              className="h-[34px] px-4 rounded-lg bg-[#FA3F58] text-[13px] text-white font-semibold hover:opacity-90 transition-opacity"
            >
              Add term
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <ErrorState message={error} onRetry={() => fetchTerms(applied, page, limit)} />
      ) : (
        <>
          <TermsTable
            rows={items}
            loading={loading}
            busyId={busyId}
            onEdit={(term) => setModal({ term })}
            onToggleActive={handleToggleActive}
            onDelete={handleDelete}
          />
          {!loading && total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={limit}
              total={total}
              pageSizeOptions={[25, 50, 100]}
              onPageSizeChange={(size) => writeParams({ limit: size, page: 1 })}
              onPrev={() => writeParams({ page: page - 1 })}
              onNext={() => writeParams({ page: page + 1 })}
              onGoToPage={(p) => writeParams({ page: p })}
            />
          )}
        </>
      )}

      <TermModal
        visible={!!modal}
        term={modal?.term}
        onClose={() => setModal(null)}
        onSubmit={handleSubmitModal}
        submitting={submitting}
      />
    </Col>
  );
};

export default ProfanityTab;
